import { getMCPMonitorTopTerms } from "@/api/endpoints/searches";
import { useI18n } from "@/contexts/i18nContext";
import { cn } from "@/lib/utils";
import { MCPMonitorTopTerms } from "@/types/Search";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import { Badge } from "@components/ui/badge";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
  WidgetTitle,
} from "@components/ui/widget";
import { getMCPMonitorFiltersFromSearchFilterV4 } from "@components/widgets/utils";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownRightIcon, ArrowUpRightIcon } from "lucide-react";

export default function TopTermsWidget({
  diffInDays,
  searchUuid,
  className,
  filters,
  ...rest
}: WidgetProps & {
  searchUuid: Uuid;
  filters: SearchFilterV4WithDates;
  diffInDays: number;
}) {
  // Server state
  const { data: topTerms, isLoading: isTopTermsLoading } = useQuery({
    queryKey: [searchUuid, "insights-results", "top-terms", filters],
    queryFn: () =>
      getMCPMonitorTopTerms(
        searchUuid,
        getMCPMonitorFiltersFromSearchFilterV4(filters)
      ),
  });

  const { data: previousTopTerms, isLoading: isPreviousTopTermsLoading } =
    useQuery({
      queryKey: [searchUuid, "insights-results", "previous-top-terms", filters],
      queryFn: () =>
        getMCPMonitorTopTerms(
          searchUuid,
          getMCPMonitorFiltersFromSearchFilterV4(filters, diffInDays + 1)
        ),
    });

  const isLoading = isTopTermsLoading || isPreviousTopTermsLoading;

  return (
    <Widget className={cn(className, "min-h-64")} {...rest}>
      <WidgetHeader isLoading={isLoading} skeletonClassName="h-11.5">
        <div className="flex flex-col gap-0.5">
          <WidgetTitle>
            <Trans>Relaterade ord</Trans>
          </WidgetTitle>

          <p className="text-muted-foreground text-sm">
            <Trans>Ord som förekommer tillsammans med dina sökord</Trans>
          </p>
        </div>
      </WidgetHeader>
      <WidgetContent isLoading={isLoading} className="overflow-y-auto">
        {topTerms?.ok && previousTopTerms?.ok ? (
          <Terms
            previousTerms={previousTopTerms.data.terms}
            terms={topTerms.data.terms}
          />
        ) : null}
      </WidgetContent>
    </Widget>
  );
}

function Terms({
  previousTerms,
  terms,
}: {
  previousTerms: MCPMonitorTopTerms["terms"];
  terms: MCPMonitorTopTerms["terms"];
}) {
  const { formatPercent, abbreviateNumber } = useI18n();

  const total = terms.reduce((a, b) => a + b.doc_count, 0);

  return (
    <div className="flex items-center flex-wrap gap-3">
      {terms
        .toSorted((a, b) => b.doc_count - a.doc_count)
        .map((term) => {
          const share = term.doc_count / total;
          const previousTerm = previousTerms.find((t) => t.term === term.term);

          const delta = previousTerm
            ? (term.doc_count - previousTerm.doc_count) / previousTerm.doc_count
            : undefined;

          const isNew = delta === undefined;
          const isUnchangedDelta = (delta ?? 0) === 0;
          const isPositiveDelta = (delta ?? 0) > 0;
          const isNegativeDelta = (delta ?? 0) < 0;

          return (
            <div
              key={term.term}
              className={cn(
                "pl-3 pr-2 py-2 flex items-center gap-2 rounded-lg",
                isNew || isPositiveDelta || isNegativeDelta ? "pr-2" : "pr-3",
                {
                  "bg-purple-100": isUnchangedDelta,
                  "bg-lime-100": isPositiveDelta,
                  "bg-pink-100": isNegativeDelta,
                }
              )}
            >
              <p
                className={cn("font-medium leading-tight", {
                  "text-xs": share < 0.1,
                  "text-sm": share >= 0.1,
                })}
              >
                {term.term} • {abbreviateNumber(term.doc_count)}
              </p>

              {delta !== 0 ? (
                <Badge
                  shape="square"
                  variant={isNegativeDelta ? "pink" : delta ? "lime" : "purple"}
                  intensity="strong"
                >
                  {isNew ? (
                    "New"
                  ) : (
                    <>
                      {isPositiveDelta ? (
                        <ArrowUpRightIcon />
                      ) : (
                        <ArrowDownRightIcon />
                      )}
                      {formatPercent(delta, 0)}
                    </>
                  )}
                </Badge>
              ) : null}
            </div>
          );
        })}
    </div>
  );
}
